import { useState } from "react"
import { Card, Button, Select } from "antd"
import { Plus } from "lucide-react"
import AddPriorityModal from "./AddPriorityModal"

const { Option } = Select

const METRIC_OPTIONS = [
  {
    group: "Growth & Revenue",
    metrics: [
      "Monthly Recurring Revenue (MRR)",
      "Revenue Growth Rate",
      "Customer Acquisition Cost (CAC)",
      "Customer Lifetime Value (LTV)",
      "CAC-to-LTV Ratio",
      "Gross Margin %",
    ],
  },
  {
    group: "Financial Efficiency",
    metrics: [
      "Burn Rate",
      "Runway (Months Remaining)",
      "Net Profit Margin",
      "Operating Cash Flow",
      "Cost of Goods Sold (COGS)",
      "Budget Utilization %",
    ],
  },
  {
    group: "Operational Health",
    metrics: [
      "Task Completion Rate",
      "Team Capacity Utilization",
      "Tool Utilization Efficiency (e.g., underused software)",
      "Average Task Cycle Time",
      "SLA Breach Rate",
      "Employee Churn Rate",
    ],
  },
  {
    group: "Sales & Pipeline",
    metrics: [
      "Qualified Leads per Week",
      "Sales Conversion Rate",
      "Sales Velocity",
      "Pipeline Coverage Ratio",
      "Average Deal Size",
      "Churn Rate (Customer/Revenue)",
    ],
  },
  {
    group: "Marketing Signals",
    metrics: [
      "Cost per Lead (CPL)",
      "Customer Acquisition by Channel",
      "Conversion Rate by Channel",
      "Website to Lead Conversion %",
      "Engagement Rate (Email, Social, Ads)",
    ],
  },
  {
    group: "Product & Engineering",
    metrics: [
      "Feature Delivery Velocity",
      "Bug Fix Time",
      "Deployment Frequency",
      "System Downtime (Uptime %)",
      "NPS (Net Promoter Score)",
      "Escalation Volume",
    ],
  },
  {
    group: "Strategic Alignment",
    metrics: [
      "Objective Progress Score (per OKR)",
      "Priority-Execution Alignment %",
      "Risk-to-Objective Ratio",
      "Strategy Effectiveness Score (post-execution ROI)",
      "Opportunity Capture Rate",
    ],
  },
]

export default function RadarPriorityControl({ priorities, onPrioritiesChange }) {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleMetricChange = (index, newMetric) => {
    const updatedPriorities = [...priorities]
    updatedPriorities[index].metric = newMetric
    onPrioritiesChange(updatedPriorities)
  }

  const handleAddPriority = (newPriority) => {
    const colors = ["#4DC98B", "#D83B5C", "#FFB650", "#4179D4"]
    const newColor = colors[priorities.length % colors.length]
    onPrioritiesChange([...priorities, { ...newPriority, colorClass: newColor }])
  }

  return (
    <Card
      bordered={false}
      headStyle={{
        borderBottom: "1px solid #e5e7eb",
        padding: "16px 16px 12px 16px",
      }}
      bodyStyle={{ padding: "16px" }}
      title={<div className="text-sm">RADAR PRIORITY CONTROL</div>}
      className="w-full"
    >
      <div className="flex flex-col gap-3">
        {priorities.map((priority, index) => {
          const currentGroupMetrics = METRIC_OPTIONS.find((opt) => opt.group === priority.group)?.metrics || []
          return (
            <div
              key={`${priority.group}-${priority.metric}`}
              className="flex flex-col gap-2 sm:flex-row sm:items-center border-b border-gray-200 pb-4 last:border-b-0"
            >
              {/* Colored dot and Group Label */}
              <div className="flex items-center gap-2 w-full sm:w-40">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: priority.colorClass }} />
                <p className="text-sm font-medium truncate">{priority.group}</p>
              </div>

              {/* Metric Select dropdown */}
              <Select
                value={priority.metric}
                onChange={(newMetric) => handleMetricChange(index, newMetric)}
                className="flex-1 text-xs"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              >
                {currentGroupMetrics.map((metric) => (
                  <Option key={metric} value={metric}>
                    {metric}
                  </Option>
                ))}
              </Select>
            </div>
          )
        })}

        <Button
          className="w-full text-sm bg-transparent mt-2"
          icon={<Plus className="w-4 h-4" />}
          onClick={() => setIsModalOpen(true)}
        >
          Add Priority
        </Button>
      </div>

      <AddPriorityModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAddPriority={handleAddPriority}
        metricOptions={METRIC_OPTIONS}
      />
    </Card>
  )
}
